
import { useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Warehouse,
  Package2,
  IndianRupee,
  Layers3,
  RefreshCcw,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  BadgePercent,
  MapPin,
  Boxes,
  CheckCircle2,
  History,
  CircleDot,
  ChevronDown,
} from "lucide-react";
import { productsService } from "../../../services/products.service";
import { warehouseService } from "../../../services/warehouse.service";
import { pricingService } from "../../../services/pricing.service";
import { env } from "../../../config/env";
import ThemeMessagePopup from "./component/ThemeMessagePopup";
import { appConfirm } from "../../../utils/appPopup";

const PAGE_SIZE = 12;


function PricingFilterDropdown({
  value,
  onChange,
  options = [],
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0] ||
    { value: "", label: "Select" };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[44px] w-full items-center justify-between rounded-[12px] border bg-white px-4 text-left text-[13px] outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d7db]"
            : "border-[#f2c6cc] text-slate-600 hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{selected.label}</span>
        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[50px] z-[220] max-h-[260px] overflow-y-auto rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={String(option.value || "all")}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[10px] px-3 py-2.5 text-left text-[13px] transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const QUICK_PRICE_DURATIONS = [
  { label: "1 Month", months: 1 },
  { label: "2 Months", months: 2 },
  { label: "6 Months", months: 6 },
];

function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInputValue(value) {
  if (!value) return new Date();
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function addCalendarMonths(date, months) {
  const source = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const targetMonth = source.getMonth() + months;
  const result = new Date(source);
  result.setMonth(targetMonth);

  const normalizedTargetMonth = ((targetMonth % 12) + 12) % 12;
  if (result.getMonth() !== normalizedTargetMonth) {
    result.setDate(0);
  }

  return result;
}

function normalizeArray(input) {
  if (Array.isArray(input)) return input;
  if (Array.isArray(input?.items)) return input.items;
  if (Array.isArray(input?.data)) return input.data;
  if (Array.isArray(input?.rows)) return input.rows;
  if (Array.isArray(input?.result)) return input.result;
  return [];
}

function dedupeById(items = [], fallbackPrefix = "item") {
  const list = Array.isArray(items) ? items : [];
  const seen = new Set();

  return list.filter((item, index) => {
    const rawId = item?.id ?? item?._id ?? item?.productId ?? item?.priceId ?? `${fallbackPrefix}-${index}`;
    const key = String(rawId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getTotalCount(input, fallbackLength = 0) {
  const raw =
    input?.total ??
    input?.count ??
    input?.totalCount ??
    input?.pagination?.total ??
    input?.meta?.total ??
    input?.data?.total;

  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallbackLength;
}


function getProductVariants(product) {
  const list = Array.isArray(product?.variants) && product.variants.length
    ? product.variants
    : [{
      id: product?.defaultVariantId || "",
      variantName: product?.variantName || [product?.weightValue || product?.weight_value, product?.weightUnitSymbol || product?.weight_unit_symbol].filter(Boolean).join("") || "Default",
      label: product?.variantName || [product?.weightValue || product?.weight_value, product?.weightUnitSymbol || product?.weight_unit_symbol].filter(Boolean).join(" ") || "Default",
      isDefault: true,
      mrp: product?.mrp ?? "",
      sellingPrice: product?.sellingPrice ?? product?.selling_price ?? "",
    }];
  return list;
}

function getPreferredVariant(product) {
  const variants = getProductVariants(product);
  return variants.find((x) => x?.isDefault) || variants[0] || null;
}

function toImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function formatMoney(value, currency = "₹") {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${currency}${num.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getFriendlyPricingError(error, fallback = "Something went wrong. Please try again.") {
  const rawMessage = String(
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    ""
  ).toLowerCase();

  if (rawMessage.includes("could not determine data type of parameter")) {
    return "Price update failed due to a backend query issue. Please ask developer to check pricing update SQL parameters.";
  }

  if (rawMessage.includes("invalid input syntax for type uuid")) {
    return "Invalid pricing record selected. Please refresh the page and try again.";
  }

  if (rawMessage.includes("not found")) {
    return "This price record was not found. Please refresh the list.";
  }

  if (rawMessage.includes("permission") || rawMessage.includes("forbidden") || rawMessage.includes("unauthorized")) {
    return "You do not have permission to update this price.";
  }

  return fallback;
}

function getDiscountPercent(mrp, sellingPrice, discountPercent) {
  const apiDiscount = Number(discountPercent);
  if (Number.isFinite(apiDiscount) && apiDiscount > 0) return apiDiscount;

  const mrpNum = Number(mrp);
  const sellingNum = Number(sellingPrice);

  if (!Number.isFinite(mrpNum) || !Number.isFinite(sellingNum) || mrpNum <= 0 || sellingNum >= mrpNum) {
    return 0;
  }

  return ((mrpNum - sellingNum) / mrpNum) * 100;
}

function getProductSubtitle(product) {
  const parts = [
    product?.brandName || product?.brand || "",
    product?.categoryName || product?.category || "",
    product?.unitValue && product?.unitName ? `${product.unitValue} ${product.unitName}` : "",
  ].filter(Boolean);

  return parts.join(" • ");
}

function sortPricesByRecency(prices = []) {
  return [...prices].sort((a, b) => {
    const aDate = new Date(a?.effectiveFrom || a?.createdAt || a?.updatedAt || 0).getTime() || 0;
    const bDate = new Date(b?.effectiveFrom || b?.createdAt || b?.updatedAt || 0).getTime() || 0;
    return bDate - aDate;
  });
}

function extractPrimaryPrice(prices = [], selectedWarehouseId = "") {
  const list = sortPricesByRecency(Array.isArray(prices) ? prices : []);
  const warehouseSpecific = selectedWarehouseId
    ? sortPricesByRecency(list.filter((item) => String(item?.warehouseId || "") === String(selectedWarehouseId)))
    : [];

  const globalPrices = sortPricesByRecency(list.filter((item) => !item?.warehouseId));
  const activeWarehousePrice = warehouseSpecific.find((item) => item?.active);
  const activeGlobalPrice = globalPrices.find((item) => item?.active);
  const latestWarehousePrice = warehouseSpecific[0] || null;
  const latestGlobalPrice = globalPrices[0] || null;

  return {
    selected:
      activeWarehousePrice ||
      activeGlobalPrice ||
      latestWarehousePrice ||
      latestGlobalPrice ||
      list[0] ||
      null,
    warehouseSpecific,
    globalPrices,
    all: list,
  };
}

function SummaryCard({ title, value, subtext, tone = "pink" }) {
  const tones = {
    pink: {
      background: "#fff8f9",
      border: "#f2c6cc",
      title: "#d86a78",
      value: "#d86a78",
      subtext: "#b96a75",
    },
    yellow: {
      background: "#fffaf0",
      border: "#f1dfb8",
      title: "#c58b13",
      value: "#b7791f",
      subtext: "#a98238",
    },
    green: {
      background: "#f6fff7",
      border: "#cfe7d1",
      title: "#69a65f",
      value: "#5b9a50",
      subtext: "#6f9b69",
    },
    slate: {
      background: "#f8fafc",
      border: "#e2e8f0",
      title: "#64748b",
      value: "#334155",
      subtext: "#94a3b8",
    },
  };

  const colors = tones[tone] || tones.pink;

  return (
    <div
      className="rounded-[18px] px-5 py-4"
      style={{
        background: colors.background,
        border: `1px solid ${colors.border}`,
      }}
    >
      <p
        className="text-[12px] font-semibold uppercase tracking-wide"
        style={{ color: colors.title }}
      >
        {title}
      </p>

      <h3
        className="mt-1 text-[34px] font-bold leading-none sm:text-[38px]"
        style={{ color: colors.value }}
      >
        {value}
      </h3>

      <p
        className="mt-2 text-[11px] font-medium"
        style={{ color: colors.subtext }}
      >
        {subtext}
      </p>
    </div>
  );
}

function EmptyState({ warehouseName }) {
  return (
    <div className="rounded-[24px] border border-dashed border-[#efc9cf] bg-white px-6 py-14 text-center shadow-sm">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fff0f3] text-[#d86a78]">
        <Package2 size={28} />
      </div>
      <h3 className="mt-4 text-[22px] font-bold text-slate-800">No products found</h3>
      <p className="mx-auto mt-2 max-w-[560px] text-[14px] leading-6 text-slate-500">
        {warehouseName
          ? `No pricing products were found for ${warehouseName}. Try another warehouse or clear the search filter.`
          : "No products are available right now. Try searching with another keyword or refresh the list."}
      </p>
    </div>
  );
}

function PriceBadge({ active }) {
  return (
    <span
      className={
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold " +
        (active ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600")
      }
    >
      <span className={"h-2 w-2 rounded-full " + (active ? "bg-emerald-500" : "bg-rose-500")} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function PriceTimeline({ price }) {
  if (!price) return null;

  return (
    <div className="grid grid-cols-2 gap-3 rounded-[16px] bg-[#fff8f9] p-3 sm:grid-cols-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Type</p>
        <p className="mt-1 text-[13px] font-semibold text-slate-700">
          {price?.warehouseId ? "Warehouse Price" : "Global Price"}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Effective From</p>
        <p className="mt-1 text-[13px] font-semibold text-slate-700">{formatDate(price?.effectiveFrom)}</p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Effective To</p>
        <p className="mt-1 text-[13px] font-semibold text-slate-700">{formatDate(price?.effectiveTo)}</p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Currency</p>
        <p className="mt-1 text-[13px] font-semibold text-slate-700">{price?.currency || "INR"}</p>
      </div>
    </div>
  );
}

function ProductPriceCard({
  product,
  priceInfo,
  selectedWarehouseName,
  onOpenCreate,
  onTogglePrice,
  onDeletePrice,
  actionLoadingId,
}) {
  const imageUrl = toImageUrl(product?.imageUrl || product?.image_url || product?.image);
  const price = priceInfo?.selected;
  const mrp = Number(price?.mrp ?? product?.mrp);
  const sellingPrice = Number(price?.sellingPrice ?? product?.sellingPrice);
  const discount = getDiscountPercent(mrp, sellingPrice, price?.discountPercent);
  const subtitle = getProductSubtitle(product);

  return (
    <div className="overflow-hidden rounded-[26px] border border-[#f2d9dd] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.06)] transition hover:-translate-y-[2px] hover:shadow-[0_16px_40px_rgba(216,106,120,0.12)]">
      <div className="border-b border-[#f6e1e4] bg-[linear-gradient(135deg,#fff7f8_0%,#ffffff_62%)] p-5">
        <div className="flex items-start gap-4">
          <div className="h-20 w-20 overflow-hidden rounded-[18px] border border-[#f3d5da] bg-[#fff7f8] shadow-sm">
            {imageUrl ? (
              <img src={imageUrl} alt={product?.name || "product"} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[#d86a78]">
                <Package2 size={28} />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="line-clamp-2 text-[19px] font-bold text-slate-800">{product?.name || "Unnamed Product"}</h3>
                <p className="mt-1 line-clamp-2 text-[12px] text-slate-500">{subtitle || product?.shortDescription || "Product details available in catalog"}</p>
              </div>
              {price ? <PriceBadge active={!!price?.active} /> : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
              <span className="rounded-full bg-[#f7f7fb] px-3 py-1">SKU: {product?.sku || "—"}</span>
              <span className="rounded-full bg-[#f7f7fb] px-3 py-1">Barcode: {product?.barcode || "—"}</span>
              <span className="rounded-full bg-[#f7f7fb] px-3 py-1">Slug: {product?.slug || "—"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="rounded-[18px] bg-[#fff7f8] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">MRP</p>
            <p className="mt-2 text-[22px] font-bold text-slate-800">{formatMoney(mrp)}</p>
          </div>
          <div className="rounded-[18px] bg-[#fff7f8] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Selling Price</p>
            <p className="mt-2 text-[22px] font-bold text-[#d86a78]">{formatMoney(sellingPrice)}</p>
          </div>
          <div className="rounded-[18px] bg-[#fff7f8] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Discount</p>
            <p className="mt-2 text-[22px] font-bold text-slate-800">{discount > 0 ? `${discount.toFixed(1)}%` : "0%"}</p>
          </div>
          <div className="rounded-[18px] bg-[#fff7f8] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Warehouse Scope</p>
            <p className="mt-2 text-[14px] font-bold text-slate-800">{price?.warehouseId ? selectedWarehouseName || "Selected Warehouse" : "Global"}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-[18px] border border-[#f2e6e8] p-4">
            <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              <Layers3 size={15} /> Price Records
            </div>
            <p className="mt-2 text-[20px] font-bold text-slate-800">{priceInfo?.all?.length || 0}</p>
            <p className="mt-1 text-[12px] text-slate-500">
              {priceInfo?.warehouseSpecific?.length || 0} warehouse specific • {priceInfo?.globalPrices?.length || 0} global
            </p>
          </div>
          <div className="rounded-[18px] border border-[#f2e6e8] p-4">
            <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              <Boxes size={15} /> Unit & Weight
            </div>
            <p className="mt-2 text-[20px] font-bold text-slate-800">
              {product?.unitValue && product?.unitName ? `${product.unitValue} ${product.unitName}` : product?.weightValue ? `${product.weightValue} ${product?.weightUnitSymbol || ""}` : "—"}
            </p>
            <p className="mt-1 text-[12px] text-slate-500">
              Weight: {product?.weightValue ? `${product.weightValue} ${product?.weightUnitSymbol || ""}` : "Not available"}
            </p>
          </div>
          <div className="rounded-[18px] border border-[#f2e6e8] p-4">
            <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              <CircleDot size={15} /> Product Status
            </div>
            <p className="mt-2 text-[20px] font-bold text-slate-800">{product?.isActive ? "Active" : "Inactive"}</p>
            <p className="mt-1 text-[12px] text-slate-500">
              Catalog visibility: {product?.status || (product?.isActive ? "active" : "inactive") || "—"}
            </p>
          </div>
        </div>

        {price ? <PriceTimeline price={price} /> : null}

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[#f3d7db] bg-[#fffafb] px-4 py-4">
          <div>
            <p className="text-[14px] font-bold text-slate-800">
              {price ? "Current pricing is available" : "No price record found for this product"}
            </p>
            <p className="mt-1 text-[12px] text-slate-500">
              {price
                ? price?.warehouseId
                  ? `Showing warehouse specific price${selectedWarehouseName ? ` for ${selectedWarehouseName}` : ""}.`
                  : "Showing active global price for this product."
                : "Create a new price entry for global or selected warehouse scope."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenCreate(product)}
              className="inline-flex items-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#c75a69]"
            >
              <Plus size={16} /> Add Price
            </button>

            {price ? (
              <>
                <button
                  type="button"
                  onClick={() => onTogglePrice(price)}
                  disabled={actionLoadingId === price?.id}
                  className={
                    "inline-flex items-center gap-2 rounded-[12px] border px-4 py-2.5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                    (price?.active
                      ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                      : "border-emerald-200 text-emerald-600 hover:bg-emerald-50")
                  }
                >
                  <CheckCircle2 size={16} />
                  {actionLoadingId === price?.id ? "Updating..." : price?.active ? "Deactivate Price" : "Activate Price"}
                </button>

                <button
                  type="button"
                  onClick={() => onDeletePrice(price)}
                  disabled={actionLoadingId === price?.id}
                  className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 px-4 py-2.5 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X size={16} />
                  {actionLoadingId === price?.id ? "Deleting..." : "Delete Price"}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceHistory({
  prices,
  warehouseMap,
  selectedWarehouseId,
  onTogglePrice,
  onDeletePrice,
  actionLoadingId,
}) {
  if (!Array.isArray(prices) || !prices.length) return null;

  return (
    <div className="rounded-[22px] border border-[#f2d9dd] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2 text-[16px] font-bold text-slate-800">
        <Layers3 size={18} className="text-[#d86a78]" /> Price History
      </div>

      <div className="space-y-3">
        {prices.map((item) => {
          const discount = getDiscountPercent(item?.mrp, item?.sellingPrice, item?.discountPercent);
          const warehouseLabel = item?.warehouseId
            ? warehouseMap?.[String(item.warehouseId)]?.name || (String(item?.warehouseId) === String(selectedWarehouseId) ? "Selected Warehouse" : "Warehouse Price")
            : "Global";

          return (
            <div
              key={item?.id}
              className="flex flex-col gap-4 rounded-[18px] border border-[#f5e6e8] bg-[#fffdfd] p-4 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-5">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Scope</p>
                  <p className="mt-1 text-[13px] font-bold text-slate-800">{warehouseLabel}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">MRP</p>
                  <p className="mt-1 text-[13px] font-bold text-slate-800">{formatMoney(item?.mrp)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Selling</p>
                  <p className="mt-1 text-[13px] font-bold text-[#d86a78]">{formatMoney(item?.sellingPrice)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Discount</p>
                  <p className="mt-1 text-[13px] font-bold text-slate-800">{discount > 0 ? `${discount.toFixed(1)}%` : "0%"}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Duration</p>
                  <p className="mt-1 text-[13px] font-bold text-slate-800">{formatDate(item?.effectiveFrom)} - {formatDate(item?.effectiveTo)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <PriceBadge active={!!item?.active} />
                <button
                  type="button"
                  disabled={actionLoadingId === item?.id}
                  onClick={() => onTogglePrice(item)}
                  className={
                    "rounded-[12px] border px-3 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                    (item?.active
                      ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                      : "border-emerald-200 text-emerald-600 hover:bg-emerald-50")
                  }
                >
                  {actionLoadingId === item?.id ? "Updating..." : item?.active ? "Deactivate" : "Activate"}
                </button>
                <button
                  type="button"
                  onClick={() => onDeletePrice(item)}
                  disabled={actionLoadingId === item?.id}
                  className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 px-4 py-2.5 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X size={16} />
                  {actionLoadingId === item?.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CreatePriceDrawer({
  open,
  product,
  selectedWarehouseId,
  selectedWarehouseName,
  onClose,
  onSubmit,
  submitting,
}) {
  const [form, setForm] = useState({
    scope: "global",
    warehouseId: "",
    productVariantId: "",
    currency: "INR",
    mrp: "",
    sellingPrice: "",
    discountPercent: "",
    effectiveFrom: "",
    effectiveTo: "",
    active: true,
  });

  useEffect(() => {
    if (!open) return;
    const defaultVariant = getPreferredVariant(product);
    setForm({
      scope: selectedWarehouseId ? "warehouse" : "global",
      warehouseId: selectedWarehouseId || "",
      productVariantId: defaultVariant?.id || "",
      currency: "INR",
      mrp: defaultVariant?.mrp ?? product?.mrp ?? "",
      sellingPrice: defaultVariant?.sellingPrice ?? product?.sellingPrice ?? "",
      discountPercent: "",
      effectiveFrom: "",
      effectiveTo: "",
      active: true,
    });
  }, [open, product, selectedWarehouseId]);

  const change = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyDurationPreset = (months) => {
    setForm((prev) => {
      const startValue = prev.effectiveFrom || toDateInputValue(new Date());
      const startDate = parseDateInputValue(startValue);
      const endDate = addCalendarMonths(startDate, months);

      return {
        ...prev,
        effectiveFrom: startValue,
        effectiveTo: toDateInputValue(endDate),
      };
    });
  };

  const clearDurationPreset = () => {
    setForm((prev) => ({
      ...prev,
      effectiveFrom: "",
      effectiveTo: "",
    }));
  };

  const isDurationPresetActive = (months) => {
    if (!form.effectiveFrom || !form.effectiveTo) return false;
    const expectedTo = toDateInputValue(addCalendarMonths(parseDateInputValue(form.effectiveFrom), months));
    return form.effectiveTo === expectedTo;
  };

  const currentDiscount = getDiscountPercent(form.mrp, form.sellingPrice, form.discountPercent);

  if (!open || !product) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex justify-end bg-black/35">
      <div className="flex h-full w-full max-w-[560px] flex-col overflow-hidden bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#f3d9dd] bg-[#fff8f9] px-6 py-5">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#d86a78]">Save Price</p>
            <h2 className="mt-1 text-[24px] font-bold text-slate-800">{product?.name || "Product"}</h2>
            <p className="mt-1 text-[13px] text-slate-500">Create a new price or update the existing row for the same scope.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition hover:bg-white hover:text-slate-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
          <div className="rounded-[18px] border border-[#f2d9dd] bg-[#fffafb] p-4">
            <p className="text-[13px] font-semibold text-slate-700">Pricing Scope</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => change("scope", "global")}
                className={
                  "rounded-[14px] border px-4 py-3 text-left transition " +
                  (form.scope === "global"
                    ? "border-[#d86a78] bg-[#fff0f3] text-[#d86a78]"
                    : "border-[#ecd8db] bg-white text-slate-600 hover:border-[#d86a78]")
                }
              >
                <p className="text-[14px] font-bold">Global Price</p>
                <p className="mt-1 text-[12px]">Visible as common default price.</p>
              </button>
              <button
                type="button"
                onClick={() => change("scope", "warehouse")}
                className={
                  "rounded-[14px] border px-4 py-3 text-left transition " +
                  (form.scope === "warehouse"
                    ? "border-[#d86a78] bg-[#fff0f3] text-[#d86a78]"
                    : "border-[#ecd8db] bg-white text-slate-600 hover:border-[#d86a78]")
                }
              >
                <p className="text-[14px] font-bold">Warehouse Price</p>
                <p className="mt-1 text-[12px]">Attach this price to selected warehouse.</p>
              </button>
            </div>

            {form.scope === "warehouse" ? (
              <div className="mt-4 rounded-[14px] bg-white p-3 text-[13px] text-slate-600">
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <MapPin size={15} className="text-[#d86a78]" /> Warehouse
                </div>
                <p className="mt-1">{selectedWarehouseName || "Please select warehouse from filter first."}</p>
              </div>
            ) : null}
          </div>

          <div className="rounded-[18px] border border-[#f2d9dd] bg-[#fffafb] p-4">
            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Variant</span>
              <select
                value={form.productVariantId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  const variant = getProductVariants(product).find((x) => String(x.id) === String(nextId)) || null;
                  setForm((prev) => ({
                    ...prev,
                    productVariantId: nextId,
                    mrp: variant?.mrp ?? prev.mrp,
                    sellingPrice: variant?.sellingPrice ?? prev.sellingPrice,
                  }));
                }}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
              >
                {getProductVariants(product).map((variant) => (
                  <option key={variant.id || variant.label} value={variant.id || ""}>
                    {variant.label || variant.variantName || variant.name || "Default"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">MRP</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.mrp}
                onChange={(e) => change("mrp", e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
                placeholder="Enter MRP"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Selling Price</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.sellingPrice}
                onChange={(e) => change("sellingPrice", e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
                placeholder="Enter selling price"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Discount %</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.discountPercent}
                onChange={(e) => change("discountPercent", e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
                placeholder="Optional"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Currency</span>
              <input
                type="text"
                value={form.currency}
                onChange={(e) => change("currency", e.target.value.toUpperCase())}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
                placeholder="INR"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Effective From</span>
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => change("effectiveFrom", e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[13px] font-semibold text-slate-700">Effective To</span>
              <input
                type="date"
                value={form.effectiveTo}
                onChange={(e) => change("effectiveTo", e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#f0d5da] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dbe0]"
              />
            </label>

            <div className="rounded-[16px] border border-[#f2d9dd] bg-[#fffafb] p-3 sm:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[13px] font-semibold text-slate-700">Quick duration</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">Uses selected Effective From, or today if empty.</p>
                </div>
                {(form.effectiveFrom || form.effectiveTo) ? (
                  <button
                    type="button"
                    onClick={clearDurationPreset}
                    className="rounded-full px-3 py-1.5 text-[12px] font-semibold text-slate-500 transition hover:bg-white hover:text-[#d86a78]"
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                {QUICK_PRICE_DURATIONS.map((item) => {
                  const active = isDurationPresetActive(item.months);

                  return (
                    <button
                      key={item.months}
                      type="button"
                      onClick={() => applyDurationPreset(item.months)}
                      className={
                        "h-10 rounded-[12px] border px-3 text-[12px] font-bold transition " +
                        (active
                          ? "border-[#d86a78] bg-[#d86a78] text-white shadow-[0_8px_18px_rgba(216,106,120,0.22)]"
                          : "border-[#f0d5da] bg-white text-slate-600 hover:border-[#d86a78] hover:bg-[#fff0f3] hover:text-[#d86a78]")
                      }
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-[18px] border border-[#f2d9dd] bg-[#fffafb] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[14px] font-bold text-slate-800">Price preview</p>
                <p className="mt-1 text-[12px] text-slate-500">Same product + same scope will update the existing row.</p>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.active}
                  onChange={(e) => change("active", e.target.checked)}
                />
                Keep active
              </label>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-[14px] bg-white p-3">
                <p className="text-[11px] text-slate-400">MRP</p>
                <p className="mt-1 text-[16px] font-bold text-slate-800">{formatMoney(form.mrp)}</p>
              </div>
              <div className="rounded-[14px] bg-white p-3">
                <p className="text-[11px] text-slate-400">Selling</p>
                <p className="mt-1 text-[16px] font-bold text-[#d86a78]">{formatMoney(form.sellingPrice)}</p>
              </div>
              <div className="rounded-[14px] bg-white p-3">
                <p className="text-[11px] text-slate-400">Discount</p>
                <p className="mt-1 text-[16px] font-bold text-slate-800">{currentDiscount > 0 ? `${currentDiscount.toFixed(1)}%` : "0%"}</p>
              </div>
              <div className="rounded-[14px] bg-white p-3">
                <p className="text-[11px] text-slate-400">Scope</p>
                <p className="mt-1 text-[16px] font-bold text-slate-800">{form.scope === "warehouse" ? "Warehouse" : "Global"}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#f3d9dd] px-6 py-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[12px] border border-[#ecd3d8] px-5 py-2.5 text-[13px] font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting || (form.scope === "warehouse" && !selectedWarehouseId)}
              onClick={() =>
                onSubmit({
                  productId: product?.id,
                  productVariantId: form.productVariantId || undefined,
                  warehouseId: form.scope === "warehouse" ? selectedWarehouseId : undefined,
                  currency: form.currency || "INR",
                  mrp: form.mrp,
                  sellingPrice: form.sellingPrice,
                  discountPercent: form.discountPercent,
                  effectiveFrom: form.effectiveFrom || undefined,
                  effectiveTo: form.effectiveTo || undefined,
                  active: form.active,
                })
              }
              className="rounded-[12px] bg-[#d86a78] px-5 py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#c75a69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Saving..." : "Save Price"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPricing() {
  const navigate = useNavigate();
  const [warehouseId, setWarehouseId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [expandedProductId, setExpandedProductId] = useState("");

  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [priceMap, setPriceMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");

  const [popup, setPopup] = useState({ open: false, type: "info", title: "", message: "" });

  const warehouseMap = useMemo(() => {
    return (warehouses || []).reduce((acc, item) => {
      acc[String(item?.id)] = item;
      return acc;
    }, {});
  }, [warehouses]);

  const selectedWarehouse = warehouseId ? warehouseMap[String(warehouseId)] : null;
  const isGlobalView = !warehouseId;
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  const openPopup = useCallback((type, title, message) => {
    setPopup({ open: true, type, title, message });
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      const res = await warehouseService.listWarehouses({ page: 1, limit: 200 });
      setWarehouses(normalizeArray(res));
    } catch (error) {
      openPopup("error", "Warehouse load failed", error?.message || "Unable to fetch warehouse list.");
    }
  }, [openPopup]);

  const loadProductsAndPrices = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) setRefreshing(true);
        else setLoading(true);

        const productRes = await productsService.listProducts({
          page,
          limit: PAGE_SIZE,
          warehouseId,
          q: query,
        });

        const list = dedupeById(normalizeArray(productRes), "product");
        setProducts(list);
        setTotalCount(getTotalCount(productRes, list.length));

        const priceEntries = await Promise.all(
          list.map(async (product) => {
            try {
              const res = await pricingService.listPrices({
                productId: product?.id,
                warehouseId,
              });

              return [String(product?.id), dedupeById(normalizeArray(res), "price")];
            } catch {
              return [String(product?.id), []];
            }
          })
        );

        setPriceMap(Object.fromEntries(priceEntries));
      } catch (error) {
        setProducts([]);
        setPriceMap({});
        setTotalCount(0);
        openPopup("error", "Pricing load failed", error?.message || "Unable to load pricing data.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [openPopup, page, warehouseId, query]
  );

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadProductsAndPrices();
  }, [loadProductsAndPrices]);

  useEffect(() => {
    setExpandedProductId("");
  }, [warehouseId, query, page]);

  const pricingStats = useMemo(() => {
    let activePriceCount = 0;
    let warehousePriceCount = 0;
    let globalPriceCount = 0;

    products.forEach((product) => {
      const prices = priceMap[String(product?.id)] || [];
      prices.forEach((item) => {
        if (item?.active) activePriceCount += 1;
        if (item?.warehouseId) warehousePriceCount += 1;
        else globalPriceCount += 1;
      });
    });

    return { activePriceCount, warehousePriceCount, globalPriceCount };
  }, [products, priceMap]);

  const onSearchSubmit = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setQuery(searchText.trim());
  };

  const onRefresh = () => {
    loadProductsAndPrices({ silent: true });
  };

  const handleCreatePrice = async (payload) => {
    try {
      if (!payload?.productVariantId) {
        openPopup("error", "Variant missing", "Please select a valid variant before saving price.");
        return;
      }
      if (!payload?.mrp || !payload?.sellingPrice) {
        openPopup("error", "Required fields missing", "MRP and selling price are required.");
        return;
      }
      if (Number(payload.sellingPrice) > Number(payload.mrp)) {
        openPopup("error", "Invalid price", "Selling price cannot be greater than MRP.");
        return;
      }
      setSubmitLoading(true);
      const response = await pricingService.createPrice({
        ...payload,
        mrp: Number(payload.mrp),
        sellingPrice: Number(payload.sellingPrice),
        discountPercent:
          payload.discountPercent === "" || payload.discountPercent === undefined
            ? undefined
            : Number(payload.discountPercent),
      });

      const savedMode = response?.mode || response?.item?.mode || "created";
      openPopup(
        "success",
        savedMode === "updated" ? "Price updated" : "Price created",
        savedMode === "updated"
          ? "Existing price row was updated successfully. No duplicate row was created."
          : "New pricing record has been saved successfully."
      );
      setDrawerOpen(false);
      setSelectedProduct(null);
      loadProductsAndPrices({ silent: true });
    } catch (error) {
      openPopup(
        "error",
        "Price save failed",
        getFriendlyPricingError(
          error,
          "Unable to save price right now. Please check MRP, selling price and variant."
        )
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (!popup.open) return undefined;

    const timer = window.setTimeout(() => {
      setPopup((prev) => ({ ...prev, open: false }));
    }, 3200);

    return () => window.clearTimeout(timer);
  }, [popup.open, popup.title, popup.message, popup.type]);

  const handleTogglePrice = async (price) => {
    if (!price?.id) return;
    try {
      setActionLoadingId(String(price.id));
      if (price?.active) await pricingService.deactivatePrice(price.id);
      else await pricingService.activatePrice(price.id);

      openPopup(
        "success",
        price?.active ? "Price deactivated" : "Price activated",
        `Pricing record has been ${price?.active ? "deactivated" : "activated"} successfully.`
      );

      loadProductsAndPrices({ silent: true });
    } catch (error) {
      openPopup(
        "error",
        "Price update failed",
        getFriendlyPricingError(
          error,
          "Unable to update price status right now. Please refresh and try again."
        )
      );
    } finally {
      setActionLoadingId("");
    }
  };

  const handleDeletePrice = async (price) => {
    if (!price?.id) return;

    const ok = await appConfirm("Are you sure you want to delete this price?", { danger: true, confirmText: "DELETE" });
    if (!ok) return;

    try {
      setActionLoadingId(String(price.id));
      await pricingService.deletePrice(price.id);

      openPopup(
        "success",
        "Price deleted",
        "Pricing record has been deleted successfully."
      );

      loadProductsAndPrices({ silent: true });
    } catch (error) {
      openPopup(
        "error",
        "Price delete failed",
        getFriendlyPricingError(
          error,
          "Unable to delete this price right now. Please refresh and try again."
        )
      );
    } finally {
      setActionLoadingId("");
    }
  };

  const tableRows = useMemo(() => {
    return products.map((product) => {
      const prices = dedupeById(priceMap[String(product?.id)] || [], "price");
      const priceInfo = extractPrimaryPrice(prices, warehouseId);
      const selectedPrice = priceInfo?.selected || null;
      const discount = getDiscountPercent(
        selectedPrice?.mrp ?? product?.mrp,
        selectedPrice?.sellingPrice ?? product?.sellingPrice,
        selectedPrice?.discountPercent
      );
      const filteredHistory = sortPricesByRecency(
        prices.filter((item) => {
          if (!warehouseId) return true;
          return !item?.warehouseId || String(item?.warehouseId) === String(warehouseId);
        })
      );

      return {
        product,
        prices,
        priceInfo,
        selectedPrice,
        discount,
        filteredHistory,
      };
    });
  }, [products, priceMap, warehouseId]);

  return (
    <div className="w-full max-w-full overflow-hidden px-0">
      <div className="mx-auto max-w-[1650px] space-y-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold tracking-wide text-[#d86a78] sm:text-[25px]">
              PRODUCT PRICING
            </h1>
            <p className="mt-1 max-w-[840px] text-[13px] font-medium leading-5 text-slate-500">
              Manage global and warehouse-based product pricing from one place.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Selected Scope
              </div>
              <div className="mt-0.5 max-w-[180px] truncate text-[13px] font-bold text-slate-700">
                {selectedWarehouse?.name || "Global Catalog"}
              </div>
            </div>

            <div className="rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Visible Products
              </div>
              <div className="mt-0.5 text-[13px] font-bold text-slate-700">
                {products.length}
              </div>
            </div>

            <div className="rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Active Price Records
              </div>
              <div className="mt-0.5 text-[13px] font-bold text-slate-700">
                {pricingStats.activePriceCount}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Total Products"
            value={totalCount}
            subtext={selectedWarehouse ? "Products filtered by selected warehouse" : "Showing global catalog products"}
            tone="pink"
          />
          <SummaryCard
            title="Warehouse Prices"
            value={pricingStats.warehousePriceCount}
            subtext="Warehouse specific records on this page"
            tone="yellow"
          />
          <SummaryCard
            title="Global Prices"
            value={pricingStats.globalPriceCount}
            subtext="Default prices available on this page"
            tone="green"
          />
          <SummaryCard
            title="Current Page"
            value={`${page}/${totalPages}`}
            subtext="Table pagination for large product list"
            tone="slate"
          />
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-transparent p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(320px,1.6fr)_minmax(260px,1fr)_auto_auto] xl:items-end">
            <form onSubmit={onSearchSubmit} className="relative min-w-0">
              <label className="mb-2 block text-[12px] font-semibold text-slate-600">
                Search Product
              </label>
              <Search
                size={16}
                className="pointer-events-none absolute left-4 top-[50px] -translate-y-1/2 text-slate-400"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by product name, barcode, SKU"
                className="h-[44px] w-full rounded-[12px] border border-[#f2c6cc] bg-white pl-10 pr-4 text-[13px] text-slate-600 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
              />
            </form>

            <div className="min-w-0">
              <label className="mb-2 block text-[12px] font-semibold text-slate-600">
                Select Warehouse
              </label>
              <PricingFilterDropdown
                value={warehouseId}
                onChange={(value) => {
                  setWarehouseId(value);
                  setPage(1);
                }}
                options={[
                  { value: "", label: "All products (global view)" },
                  ...warehouses.map((item) => ({
                    value: item?.id || "",
                    label: item?.name || item?.warehouse_name || "Warehouse",
                  })),
                ]}
              />
            </div>

            <button
              type="button"
              onClick={onSearchSubmit}
              className="inline-flex h-[44px] items-center justify-center gap-2 rounded-full bg-[#d86a78] px-5 text-[13px] font-semibold text-white transition hover:bg-[#c95a69]"
            >
              <Search size={15} />
              Search
            </button>

            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex h-[44px] items-center justify-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-5 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff7f8]"
            >
              <RefreshCcw size={15} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#fff1f3] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
              {selectedWarehouse ? `Warehouse: ${selectedWarehouse?.name || "Selected"}` : "Global products visible"}
            </span>
            {query ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
                Search: {query}
              </span>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 rounded-[24px] border border-[#f2d9dd] bg-white p-4 shadow-sm">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-[16px] bg-[#fff2f4]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <EmptyState warehouseName={selectedWarehouse?.name} />
        ) : (
          <div className="overflow-hidden rounded-[24px] border border-[#f2d9dd] bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr className="bg-[#fff4f6] text-left">
                    <th className="px-5 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Product</th>
                    {/* <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Scope</th> */}
                    <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">MRP</th>
                    <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Selling</th>
                    <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Discount</th>
                    <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Status</th>
                    <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Effective</th>
                    {/* <th className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Records</th> */}
                    <th className="px-5 py-4 text-right text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map(({ product, selectedPrice, discount, filteredHistory, priceInfo }) => {
                    const imageUrl = toImageUrl(product?.imageUrl || product?.image_url || product?.image);
                    const subtitle = getProductSubtitle(product);
                    const scopeName = selectedPrice?.warehouseId
                      ? warehouseMap[String(selectedPrice?.warehouseId)]?.name || selectedWarehouse?.name || "Warehouse"
                      : "Global";
                    const rowOpen = expandedProductId === String(product?.id);
                    const rowActiveId = selectedPrice?.id ? String(selectedPrice.id) : "";
                    const canManageSelectedPrice =
                      !!warehouseId &&
                      !!selectedPrice?.id &&
                      !!selectedPrice?.warehouseId &&
                      String(selectedPrice.warehouseId) === String(warehouseId);

                    return (
                      <React.Fragment key={product?.id}>
                        <tr className="border-t border-[#f7e5e8] align-top transition hover:bg-[#fffafb]">
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              onClick={() => setExpandedProductId(rowOpen ? "" : String(product?.id))}
                              className="flex w-full items-center gap-3 text-left"
                            >
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-[#f0d9dd] bg-[#fff6f7]">
                                {imageUrl ? (
                                  <img src={imageUrl} alt={product?.name || "product"} className="h-full w-full object-cover" />
                                ) : (
                                  <Package2 size={20} className="text-[#d86a78]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-[15px] font-bold text-slate-800">{product?.name || "Unnamed product"}</p>
                                <p className="mt-1 text-[12px] text-slate-500">{subtitle || "Product details not available"}</p>
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1">SKU: {product?.sku || "—"}</span>
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1">Barcode: {product?.barcode || "—"}</span>
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1">Slug: {product?.slug || "—"}</span>
                                </div>
                              </div>
                            </button>
                          </td>

                          {/* <td className="px-4 py-4">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                              <MapPin size={13} /> {scopeName}
                            </div>
                          </td> */}

                          <td className="px-4 py-4 text-[14px] font-bold text-slate-800">
                            {formatMoney(selectedPrice?.mrp ?? product?.mrp)}
                          </td>

                          <td className="px-4 py-4 text-[14px] font-bold text-[#d86a78]">
                            {formatMoney(selectedPrice?.sellingPrice ?? product?.sellingPrice)}
                          </td>

                          <td className="px-4 py-4">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#fff7f8] px-3 py-1 text-[12px] font-semibold text-slate-700">
                              <BadgePercent size={13} className="text-[#d86a78]" /> {Number(discount || 0).toFixed(1)}%
                            </div>
                          </td>

                          <td className="px-4 py-4">
                            <PriceBadge active={Boolean(selectedPrice?.active)} />
                          </td>

                          <td className="px-4 py-4 text-[13px] text-slate-600">
                            <div className="font-semibold text-slate-700">{formatDate(selectedPrice?.effectiveFrom)}</div>
                            <div className="mt-1 text-[12px] text-slate-400">to {formatDate(selectedPrice?.effectiveTo)}</div>
                          </td>

                          {/* <td className="px-4 py-4">
                            <div className="text-[15px] font-bold text-slate-800">{filteredHistory.length}</div>
                            <div className="mt-1 text-[12px] text-slate-400">
                              {warehouseId ? "selected warehouse + global" : "all price records"}
                            </div>
                          </td> */}

                          <td className="px-5 py-4">
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setDrawerOpen(true);
                                }}
                                className="inline-flex items-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2 text-[12px] font-semibold text-white transition hover:bg-[#c75a69]"
                              >
                                <Plus size={14} /> Add Price
                              </button>

                              {canManageSelectedPrice ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={actionLoadingId === rowActiveId}
                                    onClick={() => handleTogglePrice(selectedPrice)}
                                    className={
                                      "inline-flex items-center gap-2 rounded-[12px] border px-4 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                                      (selectedPrice?.active
                                        ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                                        : "border-emerald-200 text-emerald-600 hover:bg-emerald-50")
                                    }
                                  >
                                    {selectedPrice?.active ? <X size={14} /> : <CheckCircle2 size={14} />}
                                    {actionLoadingId === rowActiveId
                                      ? "Updating..."
                                      : selectedPrice?.active
                                        ? "Deactivate"
                                        : "Activate"}
                                  </button>

                                  <button
                                    type="button"
                                    disabled={actionLoadingId === rowActiveId}
                                    onClick={() => handleDeletePrice(selectedPrice)}
                                    className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 px-4 py-2 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    <X size={14} />
                                    {actionLoadingId === rowActiveId ? "Deleting..." : "Delete"}
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>

                        {rowOpen ? (
                          <tr>
                            <td colSpan={7} className="border-t border-[#f7e5e8] bg-[#fffafb] px-5 py-5">
                              <div className="rounded-[20px] border border-[#f1d7dc] bg-white p-5 shadow-sm">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <h3 className="text-[16px] font-bold text-slate-800">Price history</h3>
                                    <p className="mt-1 text-[13px] text-slate-500">
                                      {warehouseId
                                        ? `Showing ${selectedWarehouse?.name || "selected warehouse"} and global records only.`
                                        : "Showing all available global and warehouse price records."}
                                    </p>
                                  </div>

                                  <div className="rounded-[16px] bg-[#fff4f6] px-4 py-3 text-[12px] text-slate-600">
                                    <div className="font-semibold text-slate-800">Current display price</div>
                                    <div className="mt-1">{selectedPrice?.warehouseId ? "Warehouse priority" : "Global fallback or latest available"}</div>
                                  </div>
                                </div>

                                {selectedPrice ? (
                                  <div className="mt-4">
                                    <PriceTimeline price={selectedPrice} />
                                  </div>
                                ) : null}

                                <div className="mt-5 overflow-hidden rounded-[18px] border border-[#f1dfe2]">
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full">
                                      <thead className="bg-[#fff6f7]">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Scope</th>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">MRP</th>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Selling</th>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Discount</th>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Effective</th>
                                          <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Status</th>
                                          <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {filteredHistory.length ? (
                                          filteredHistory.map((item, idx) => {
                                            const itemId = String(item?.id || idx);
                                            const itemScope = item?.warehouseId
                                              ? warehouseMap[String(item?.warehouseId)]?.name || selectedWarehouse?.name || "Warehouse"
                                              : "Global";
                                            const itemDiscount = getDiscountPercent(item?.mrp, item?.sellingPrice, item?.discountPercent);
                                            const canManageHistoryPrice =
                                              !!warehouseId &&
                                              !!item?.warehouseId &&
                                              String(item.warehouseId) === String(warehouseId);

                                            return (
                                              <tr key={itemId} className="border-t border-[#f7e5e8]">
                                                <td className="px-4 py-3 text-[13px] font-semibold text-slate-700">{itemScope}</td>
                                                <td className="px-4 py-3 text-[13px] font-semibold text-slate-700">{formatMoney(item?.mrp)}</td>
                                                <td className="px-4 py-3 text-[13px] font-semibold text-[#d86a78]">{formatMoney(item?.sellingPrice)}</td>
                                                <td className="px-4 py-3 text-[13px] text-slate-700">{Number(itemDiscount || 0).toFixed(1)}%</td>
                                                <td className="px-4 py-3 text-[12px] text-slate-500">
                                                  {formatDate(item?.effectiveFrom)} - {formatDate(item?.effectiveTo)}
                                                </td>
                                                <td className="px-4 py-3"><PriceBadge active={Boolean(item?.active)} /></td>
                                                <td className="px-4 py-3 text-right">
                                                  {canManageHistoryPrice ? (
                                                    <div className="inline-flex items-center gap-2">
                                                      <button
                                                        type="button"
                                                        disabled={actionLoadingId === itemId}
                                                        onClick={() => handleTogglePrice(item)}
                                                        className={
                                                          "inline-flex items-center gap-2 rounded-[12px] border px-4 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                                                          (item?.active
                                                            ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                                                            : "border-emerald-200 text-emerald-600 hover:bg-emerald-50")
                                                        }
                                                      >
                                                        {item?.active ? <X size={14} /> : <CheckCircle2 size={14} />}
                                                        {actionLoadingId === itemId
                                                          ? "Updating..."
                                                          : item?.active
                                                            ? "Deactivate"
                                                            : "Set Active"}
                                                      </button>

                                                      <button
                                                        type="button"
                                                        disabled={actionLoadingId === itemId}
                                                        onClick={() => handleDeletePrice(item)}
                                                        className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 px-4 py-2 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                      >
                                                        <X size={14} />
                                                        {actionLoadingId === itemId ? "Deleting..." : "Delete"}
                                                      </button>
                                                    </div>
                                                  ) : null}
                                                </td>
                                              </tr>
                                            );
                                          })
                                        ) : (
                                          <tr>
                                            <td colSpan={7} className="px-4 py-6 text-center text-[13px] text-slate-500">
                                              No price history available for this product.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                <div className="mt-4 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedProductId("")}
                                    className="rounded-[12px] border border-[#ecd8dc] px-4 py-2 text-[12px] font-semibold text-slate-600 transition hover:bg-[#fff5f6]"
                                  >
                                    Close details
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-[#f4e1e4] px-5 py-4 sm:flex-row">
              <p className="text-[13px] text-slate-500">
                Showing page <span className="font-semibold text-slate-800">{page}</span> of <span className="font-semibold text-slate-800">{totalPages}</span>
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-2 rounded-[12px] border border-[#edd6da] px-4 py-2 text-[13px] font-semibold text-slate-700 transition hover:bg-[#fff7f8] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={15} /> Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[#c75a69] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <CreatePriceDrawer
        open={drawerOpen}
        product={selectedProduct}
        selectedWarehouseId={warehouseId}
        selectedWarehouseName={selectedWarehouse?.name}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedProduct(null);
        }}
        onSubmit={handleCreatePrice}
        submitting={submitLoading}
      />

      <ThemeMessagePopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}
